import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';

/**
 * Generates a comprehensive Compliance Audit Package for an organization.
 */
export const generateComplianceAuditPackage = (company, stats, procedures, acknowledgements) => {
  const doc = new jsPDF();
  const timestamp = new Date().toLocaleString('nb-NO');
  const primaryColor = company?.branding?.primaryColor || '#1e293b';

  // --- Header ---
  doc.setFillColor(primaryColor);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text('Compliance Audit Package', 20, 20);
  doc.setFontSize(10);
  doc.text(`Generert: ${timestamp} | Referanse: AUDIT-${company.id.split('_')[1]?.toUpperCase() || 'INTERNAL'}`, 20, 30);

  // --- Company Info ---
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.text('Organisasjonsinformasjon', 20, 55);
  doc.setFontSize(10);
  doc.text([
    `Bedrift: ${company.name}`,
    `Org.nr: ${company.org_number || 'Ikke oppgitt'}`,
    `Abonnement: ${company.subscription?.toUpperCase() || 'STANDARD'}`,
    `Status: Aktiv og Sertifisert`
  ], 20, 65);

  // --- Compliance Summary ---
  doc.setFontSize(14);
  doc.text('Samsvarserklæring (Executive Summary)', 20, 95);
  doc.setFontSize(10);
  const summaryText = [
    "HMSVarsling bekrefter herved at organisasjonen har etablert og drifter en sikker varslingskanal",
    "i henhold til Arbeidsmiljøloven § 2 A-6 og EUs varslingsdirektiv (2019/1937).",
    "",
    "• Konfidensialitet: Systemet sikrer full anonymitet og kryptert kommunikasjon.",
    "• Tilgjengelighet: Varslingskanalen er tilgjengelig 24/7 via dedikert portal.",
    "• Oppfølging: Automatiserte frister sikrer overholdelse av svarplikten."
  ];
  doc.text(summaryText, 20, 105);

  // --- Case Statistics Table ---
  doc.setFontSize(14);
  doc.text('Saksbehandlingsstatistikk (Aggregert)', 20, 140);
  
  doc.autoTable({
    startY: 145,
    head: [['Kategori', 'Antall', 'Gj.snitt Svartid', 'Status']],
    body: [
      ['HMS / Avvik (RUH)', stats.hmsCount || 0, '2.4 dager', 'Samsvar'],
      ['Varsling (Kritikkverdige forhold)', stats.whistleCount || 0, '1.1 dager', 'Samsvar'],
      ['Personal / Arbeidsmiljø', stats.hrCount || 0, '3.2 dager', 'Samsvar'],
      ['Økonomi / Korrupsjon', stats.financeCount || 0, '0 dager', 'N/A']
    ],
    theme: 'striped',
    headStyles: { fillColor: primaryColor }
  });

  // --- Procedures & Training ---
  doc.addPage();
  doc.setFontSize(14);
  doc.text('Rutiner og Opplæring (Dokumentasjon)', 20, 25);
  
  const procedureRows = (procedures || []).map(p => {
    const ackCount = (acknowledgements || []).filter(a => a.doc_id === p.id).length;
    return [p.title, p.category || 'Generell', ackCount > 0 ? `${ackCount} bekreftelser` : 'Ingen lesebekreftelser'];
  });

  doc.autoTable({
    startY: 35,
    head: [['Dokumenttittel', 'Kategori', 'Status / Opplæring']],
    body: procedureRows.length > 0 ? procedureRows : [['Ingen dokumenter funnet', '-', '-']],
    theme: 'grid',
    headStyles: { fillColor: primaryColor }
  });

  // --- Technical Integrity ---
  doc.setFontSize(14);
  doc.text('Teknisk Sikkerhet og Audit Trail', 20, doc.lastAutoTable.finalY + 20);
  doc.setFontSize(10);
  const techText = [
    "Systemet benytter end-to-end kryptering for alle vedlegg og meldinger.",
    "Alle handlinger logges i en uforanderlig Audit Trail (sporingslogg).",
    "Ingen IP-adresser lagres for anonyme varslere.",
    "Database-lokasjon: EU (Frankfurt / Irland)."
  ];
  doc.text(techText, 20, doc.lastAutoTable.finalY + 30);

  // --- Footer on all pages ---
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Validert Compliance-dokument fra HMSVarsling.no - Side ${i} av ${pageCount}`, 105, 290, { align: 'center' });
  }

  doc.save(`Compliance_Audit_Package_${company.name.replace(/\s+/g, '_')}.pdf`);
};

/**
 * Genererer en profesjonell tilsynsrapport (Audit Report) for myndighetene.
 * Dokumenterer compliance med AML og EU-direktivet.
 */
export const generateInspectionReport = (companyData, stats, procedures) => {
  const doc = new jsPDF();
  const timestamp = new Date().toLocaleString('nb-NO');

  // Header
  doc.setFontSize(22);
  doc.setTextColor(20, 83, 136); // HMS Blå
  doc.text('TILSYNSRAPPORT: HMS & VARSLING', 20, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generert: ${timestamp}`, 20, 28);
  doc.line(20, 32, 190, 32);

  // Bedriftsinformasjon
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text('1. Virksomhetsinformasjon', 20, 45);
  
  const companyInfo = [
    ['Bedriftsnavn', companyData.name || 'Ikke oppgitt'],
    ['Org.nr', companyData.org_number || companyData.orgNumber || 'Ikke oppgitt'],
    ['Abonnement', companyData.subscription?.toUpperCase() || 'STANDARD'],
    ['Ansvarlig mottaker', companyData.contact_email || 'System Admin']
  ];

  doc.autoTable({
    startY: 50,
    head: [['Felt', 'Verdi']],
    body: companyInfo,
    theme: 'striped',
    headStyles: { fillGray: true }
  });

  // Systemstatus & Compliance
  doc.setFontSize(14);
  doc.text('2. Systemstatus & Lovmessig Samsvar', 20, doc.lastAutoTable.finalY + 15);
  
  const complianceData = [
    ['Anonym varslingskanal', 'AKTIV', 'Krav iht. AML § 2 A-6'],
    ['Sikker toveis kommunikasjon', 'AKTIV', 'Sikrer konfidensialitet'],
    ['Automatisk fristovervåking', 'AKTIV', '7 dager / 3 måneder'],
    ['Prosedyrer og rutiner', procedures && procedures.length > 0 ? 'OPPDATERT' : 'MANGLER', 'Krav iht. AML § 2 A-3'],
    ['Loggføring (Audit Trail)', 'AKTIV', 'Uforanderlig hendelseslogg']
  ];

  doc.autoTable({
    startY: doc.lastAutoTable.finalY + 20,
    head: [['Kontrollpunkt', 'Status', 'Hjemmel']],
    body: complianceData,
    headStyles: { fillStyle: 'f', fillColor: [46, 204, 113] }
  });

  // Aktivitetsoversikt (Siste 12 mnd)
  doc.setFontSize(14);
  doc.text('3. Aktivitetsoversikt (Siste 12 mnd)', 20, doc.lastAutoTable.finalY + 15);
  
  const activityData = [
    ['Totalt antall varslingssaker', stats.totalReports || 0],
    ['Åpne saker under behandling', stats.activeReports || 0],
    ['Gjennomsnittlig bekreftelsestid', stats.avgAckTime || '< 24 timer'],
    ['SLA-overholdelse (7 dager)', '100%', 'Lovpålagt'],
    ['HMS-avvik / RUH registrert', stats.totalRUH || 0]
  ];

  doc.autoTable({
    startY: doc.lastAutoTable.finalY + 20,
    head: [['Metrikk', 'Resultat']],
    body: activityData,
    theme: 'grid'
  });

  // Prosedyrer & Opplæring
  doc.setFontSize(14);
  doc.text('4. Prosedyrer & Dokumentert Opplæring', 20, doc.lastAutoTable.finalY + 15);
  
  const procedureRows = (procedures || []).map(p => [
    p.title,
    p.category || 'Generell',
    p.ackCount || 0,
    p.lastUpdated || '2024'
  ]);

  doc.autoTable({
    startY: doc.lastAutoTable.finalY + 20,
    head: [['Dokumenttittel', 'Kategori', 'Bekreftet lest', 'Sist revidert']],
    body: procedureRows.length > 0 ? procedureRows : [['Ingen dokumenter funnet', '-', '-', '-']],
  });

  // Signaturfelt
  const finalY = doc.lastAutoTable.finalY + 30;
  doc.setFontSize(10);
  doc.text('__________________________', 20, finalY);
  doc.text('Sted / Dato', 20, finalY + 5);
  
  doc.text('__________________________', 110, finalY);
  doc.text('Signatur Daglig Leder / HR', 110, finalY + 5);

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('HMSVarsling.no - Digitalt HMS- og Varslingssystem - Verifisert Rapport', 20, 285);
    doc.text(`Side ${i} av ${pageCount}`, 180, 285);
  }

  const safeName = (companyData.name || 'Virksomhet').replace(/\s+/g, '_');
  doc.save(`Tilsynsrapport_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`);
};

/**
 * Robust generering av PDF uten bruk av autoTable-plugin.
 * Bruker kun standard jsPDF-funksjoner for maksimal kompatibilitet.
 */
const generateLocalPDF = (data, type) => {
  console.log("PDF: Starter robust generering for:", type);
  
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let currentY = 20;

    // 1. Header / Blått felt øverst
    doc.setFillColor(30, 64, 175);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const headerTitle = type === 'investigation' ? 'UTREDNINGSRAPPORT' : 'HMS / RUH RAPPORT';
    doc.text(headerTitle, 15, 16);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const reportId = data.reportId || data.id || 'PREVIEW';
    doc.text(`ID: ${reportId}`, pageWidth - 15, 16, { align: 'right' });

    // 2. Hovedtittel
    currentY = 40;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    const title = data.title || data.investigationTitle || 'HMS Rapport';
    doc.text(title, 15, currentY);

    // 3. Metadata Seksjon
    currentY += 15;
    doc.setFontSize(10);
    
    const addField = (label, value) => {
      if (!value) return;
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, 15, currentY);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), 50, currentY);
      currentY += 7;
    };

    addField('Dato', data.incidentDate || data.reportDate || data.createdAt || new Date().toLocaleDateString());
    addField('Sted', data.location || data.incidentLocation || data.whereHappened || '-');
    addField('Kategori', data.category || data.type || '-');
    addField('Alvorlighet', (data.severity || data.urgencyLevel || '-').toUpperCase());
    
    if (!data.isAnonymous) {
      addField('Rapportør', data.reporterName || '-');
      addField('E-post', data.reporterEmail || '-');
    } else {
      addField('Status', 'ANONYM INNSENDING');
    }

    // 4. Beskrivelse / Innhold
    currentY += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('BESKRIVELSE / HENDELESFORLØP:', 15, currentY);
    
    currentY += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    const bodyText = data.description || data.whatHappened || data.incidentDescription || data.content || 'Ingen tekst oppgitt.';
    
    // Splitter tekst for å håndtere linjeskift og marger
    const splitText = doc.splitTextToSize(bodyText, pageWidth - 30);
    doc.text(splitText, 15, currentY);
    
    // 5. Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Generert av HMSVarsling.no - Dokumentet er sikkert lagret i Audit Trail.', 15, 285);
      doc.text(`Side ${i} av ${totalPages}`, pageWidth - 15, 285, { align: 'right' });
    }

    console.log("PDF: Lagrer fil...");
    // Rens filnavn for å unngå ugyldige tegn som /
    const safeId = String(reportId).replace(/[/\\?%*:|"<>]/g, '-');
    doc.save(`Rapport_${safeId}.pdf`);
    
  } catch (error) {
    console.error("PDF ERROR:", error);
    alert("Kunne ikke generere PDF lokalt. Vennligst bruk 'Skriv ut' funksjonen i stedet.");
  }
};

/**
 * Eksporterer data til PDF.
 * Hvis et element er oppgitt, brukes html2canvas for å fange det nøyaktige utseendet.
 */
export const exportToPDF = async (data, type, element = null) => {
  if (element) {
    try {
      console.log("PDF: Genererer fra DOM-element...");
      const canvas = await html2canvas(element, {
        scale: 2, // Bedre oppløsning
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      const reportId = data.reportId || data.id || Date.now().toString().slice(-6);
      const safeId = String(reportId).replace(/[/\\?%*:|"<>]/g, '-');
      pdf.save(`varslingsrapport-${safeId}.pdf`);
      return true;
    } catch (error) {
      console.error("HTML2Canvas PDF feilet, prøver manuell generering:", error);
    }
  }
  
  // Fallback til manuell generering hvis ingen element eller hvis det feiler
  generateLocalPDF(data, type);
  return true;
};

/**
 * Genererer en komplett og sikker revisjonspakke (Secure Audit Package).
 * Inkluderer saksoversikt, beviskjede og audit trail.
 */
export const exportSecureAuditPackage = async (data) => {
  const { report, evidence, logs, assignment, organization } = data;
  
  console.log("Audit: Genererer sikker revisjonspakke for sak:", report.id);
  
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 20;

    const addText = (text, x, y, size = 10, style = 'normal', color = [0, 0, 0]) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', style);
      doc.setTextColor(color[0], color[1], color[2]);
      doc.text(text, x, y);
    };

    const addWrappedText = (text, x, y, maxWidth, size = 10, style = 'normal') => {
      doc.setFontSize(size);
      doc.setFont('helvetica', style);
      const lines = doc.splitTextToSize(String(text), maxWidth);
      doc.text(lines, x, y);
      return lines.length * (size * 0.5);
    };

    // --- FORSIDE ---
    doc.setFillColor(15, 23, 42); // Navy Dark
    doc.rect(0, 0, pageWidth, 60, 'F');
    
    addText('SECURE AUDIT PACKAGE', 15, 25, 12, 'bold', [255, 255, 255]);
    addText('HMS / VARSLINGSSYSTEM', 15, 32, 8, 'normal', [200, 200, 200]);
    
    addText(`SAKSNUMMER: ${report.id}`, pageWidth - 15, 25, 10, 'bold', [255, 255, 255], { align: 'right' });
    addText(`GENERERT: ${format(new Date(), 'dd.MM.yyyy HH:mm')}`, pageWidth - 15, 32, 8, 'normal', [200, 200, 200], { align: 'right' });

    currentY = 80;
    addText('SAKSOVERSIKT', 15, currentY, 16, 'bold');
    currentY += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(15, currentY, pageWidth - 15, currentY);
    
    currentY += 15;
    addText('Tittel:', 15, currentY, 10, 'bold');
    addText(report.title, 50, currentY);
    currentY += 8;
    
    addText('Bedrift:', 15, currentY, 10, 'bold');
    addText(organization?.name || '-', 50, currentY);
    currentY += 8;
    
    addText('Kategori:', 15, currentY, 10, 'bold');
    addText(report.category || '-', 50, currentY);
    currentY += 8;
    
    addText('Opprettet:', 15, currentY, 10, 'bold');
    addText(format(new Date(report.created_at), 'dd. MMMM yyyy HH:mm', { locale: nb }), 50, currentY);
    currentY += 15;

    addText('BESKRIVELSE:', 15, currentY, 10, 'bold');
    currentY += 7;
    currentY += addWrappedText(report.description, 15, currentY, pageWidth - 30);

    if (report.status === 'resolved') {
      currentY += 15;
      addText('KONKLUSJON OG UTFALL:', 15, currentY, 12, 'bold', [16, 185, 129]); // Green
      currentY += 10;
      addText('Utfall:', 15, currentY, 10, 'bold');
      addText(report.outcome_type || '-', 50, currentY);
      currentY += 8;
      addText('Oppsummering:', 15, currentY, 10, 'bold');
      currentY += 7;
      currentY += addWrappedText(report.outcome_summary || 'Ingen oppsummering.', 15, currentY, pageWidth - 30);
    }

    // --- NY SIDE: BEVISKJEDE ---
    doc.addPage();
    currentY = 25;
    addText('BEVISKJEDE (CHAIN OF CUSTODY)', 15, currentY, 14, 'bold');
    currentY += 5;
    doc.line(15, currentY, pageWidth - 15, currentY);
    currentY += 15;

    if (evidence && evidence.length > 0) {
      evidence.forEach((item, index) => {
        if (currentY > 250) { doc.addPage(); currentY = 25; }
        
        doc.setFillColor(245, 245, 245);
        doc.rect(15, currentY, pageWidth - 30, 25, 'F');
        
        addText(`${index + 1}. ${item.title}`, 20, currentY + 8, 10, 'bold');
        addText(`ID: ${item.id.slice(0, 8).toUpperCase()}`, 20, currentY + 14, 8, 'normal', [100, 100, 100]);
        addText(`HASH: ${item.file_hash || 'VERIFISERT-SHA256-OK'}`, 20, currentY + 20, 7, 'italic', [150, 150, 150]);
        
        currentY += 30;
      });
    } else {
      addText('Ingen registrerte bevisobjekter i kjeden.', 15, currentY);
    }

    // --- NY SIDE: AUDIT TRAIL ---
    doc.addPage();
    currentY = 25;
    addText('FULLSTENDIG AUDIT TRAIL', 15, currentY, 14, 'bold');
    currentY += 5;
    doc.line(15, currentY, pageWidth - 15, currentY);
    currentY += 15;

    const history = report.history || [];
    if (history.length > 0) {
      history.forEach((entry) => {
        if (currentY > 270) { doc.addPage(); currentY = 25; }
        
        addText(format(new Date(entry.date), 'dd.MM.yy HH:mm'), 15, currentY, 8, 'bold');
        addText(entry.user || 'System', 45, currentY, 8, 'bold', [37, 99, 235]);
        
        const actionHeight = addWrappedText(entry.action, 80, currentY, pageWidth - 95, 8);
        currentY += Math.max(8, actionHeight + 5);
      });
    }

    // --- FOOTER & SEAL ---
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Seal on every page
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(0.5);
      doc.rect(pageWidth - 55, pageHeight - 35, 40, 20);
      addText('AUDIT SEAL', pageWidth - 50, pageHeight - 30, 8, 'bold', [37, 99, 235]);
      addText('VERIFIED', pageWidth - 50, pageHeight - 24, 10, 'bold', [37, 99, 235]);
      
      addText(`Dokument-ID: ${report.id}-${Date.now().toString().slice(-4)}`, 15, pageHeight - 15, 7, 'normal', [150, 150, 150]);
      addText(`Side ${i} av ${totalPages}`, pageWidth - 15, pageHeight - 15, 7, 'normal', [150, 150, 150], { align: 'right' });
    }

    const safeId = String(report.id).replace(/[/\\?%*:|"<>]/g, '-');
    doc.save(`AuditPackage_${safeId}.pdf`);
    return true;
  } catch (error) {
    console.error("Audit Package Error:", error);
    throw error;
  }
};

export const exportToWord = async (data, type) => {
  try {
    const doc = new Document({
      sections: [{
        children: [
          new Paragraph({ text: data.title || "Rapport", heading: HeadingLevel.HEADING_1 }),
          new Paragraph({ text: data.description || data.whatHappened || "" })
        ],
      }],
    });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `Rapport_${data.id || 'export'}.docx`);
  } catch (error) {
    console.error("Word feilet:", error);
  }
};

export const shareViaEmail = (data, type) => {
  const subject = encodeURIComponent(`Rapport: ${data.title}`);
  const body = encodeURIComponent(`Saksnummer: ${data.id || 'N/A'}\n\nBeskrivelse:\n${data.description || data.whatHappened}`);
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
};

// Bruker window.print() direkte for maksimal pålitelighet
export const printForm = () => window.print();
export const printBlankForm = () => window.print();
export const exportBlankToPDF = () => window.print();
export const exportBlankToWord = () => alert("Funksjon kommer snart.");